import { Injectable } from '@angular/core';
import { ConfigurationService } from '@sondermind/configuration';

import { EmptyURIValue } from '../consts/consts';
import { URIPrefix } from '../enums/url-prefix.enum';
import { buildInternalDirectoryPath, IInternalDirectoryLinkContext } from '../functions/build-internal-directory-link';
import { encodeSpecialCharacters } from '../functions/fixed-encode-uri-component';
import { IInternalDirectoryLinkItem } from '../models/internal-directory-link.interface';

/** Lowercases and escapes one URL segment value. The two forms differ only in percent-encoding. */
type SegmentEncoder = (input: string) => string;

// DRIFT WARNING: this two-pass slug encoding, toLocaleLowerCase() then
// encodeURIComponent(encodeSpecialCharacters(...)), is mirrored in sitemap-service
// (src/sources/provider-directory/url-builder.ts), guarded by its slug-contract spec.
// NOTE: the sitemap mirror lowercases with toLowerCase() (locale-independent), not
// toLocaleLowerCase(); the two agree on ASCII input but can diverge on locale-sensitive
// characters. Keep the special-char + URI passes identical or the sitemap's canonical
// URLs stop matching the ones this service builds.
const encodeForHref: SegmentEncoder = (input) => (
  encodeURIComponent(encodeSpecialCharacters(input.toLocaleLowerCase()))
);

/**
 * Router-command form. `Router.createUrlTree` percent-encodes each segment itself, so this stops at
 * the special-character pass: pre-encoding here would put `%2520` on the wire where the href form
 * puts `%20`, and the directory would fail to match the value back to a filter option. A literal
 * slash needs no special handling, because the router escapes it within a segment.
 */
const encodeForRouterSegment: SegmentEncoder = (input) => encodeSpecialCharacters(input.toLocaleLowerCase());

@Injectable({
  providedIn: 'root'
})
export class DirectoryLinkService {
  constructor(
    private configurationService: ConfigurationService,
  ){}

  /**
   * Absolute href for one internal-link-module item, e.g. `<base>/sta-co/cty-denver/ins-aetna`.
   * Same base resolution as buildProviderDirectoryLink; the path comes from buildInternalDirectoryPath.
   */
  buildInternalDirectoryLink(
    item: IInternalDirectoryLinkItem,
    context: IInternalDirectoryLinkContext = {},
  ): string {
    const prodBaseUrl = 'https://www.sondermind.com/provider-directory';
    const baseUrl = this.configurationService.env.providerDirectoryBase ?? prodBaseUrl;

    return `${baseUrl}${buildInternalDirectoryPath(item, context)}`;
  }

  /**
   * Creates links to the directory service, which take in route parameters for every input.
   * Accepts optional arguments for each input.
   *
   * The returned string is percent-encoded and absolute: it is for an `href` or a
   * `location.assign`. For an in-app router navigation use `buildProviderDirectoryCommands`,
   * which leaves encoding to the router.
   */
  buildProviderDirectoryLink(
    stateAbbreviation?: string,
    city?: string,
    insuranceTitle?: string,
    genderStrings?: string[],
    modalityStrings?: string[],
    specialtyStrings?: string[],
    ageStrings?: string[],
  ): string {
    // use the directory base from the config service, but default to prod if there's trouble
    const prodBaseUrl = 'https://www.sondermind.com/provider-directory';
    const baseUrl = this.configurationService.env.providerDirectoryBase ?? prodBaseUrl;

    return baseUrl.concat(this.buildDirectoryPath(
      encodeForHref,
      stateAbbreviation,
      city,
      insuranceTitle,
      genderStrings,
      modalityStrings,
      specialtyStrings,
      ageStrings
    ));
  }

  /**
   * The same destination as `buildProviderDirectoryLink`, as router commands for an in-app
   * navigation.
   *
   * Router commands must NOT be percent-encoded: `Router.createUrlTree` encodes each segment
   * itself, so handing it the `href` form would double-encode every multi-word or parenthesized
   * value (`ins-blue%20cross` becomes `ins-blue%2520cross`) and the directory would fail to match
   * the filter back to an option. Special characters are still replaced, because the directory's
   * own parser expects that form.
   */
  buildProviderDirectoryCommands(
    stateAbbreviation?: string,
    city?: string,
    insuranceTitle?: string,
    genderStrings?: string[],
    modalityStrings?: string[],
    specialtyStrings?: string[],
    ageStrings?: string[],
  ): string[] {
    // Built segment by segment rather than by splitting a path string: a value may itself contain a
    // slash (e.g. "Optum/United-EAP"), which has to stay inside one command or the router emits a
    // fourth path segment and the directory's route matcher rejects the URL.
    const filters = this.buildFilterSegment(
      encodeForRouterSegment,
      insuranceTitle,
      genderStrings,
      modalityStrings,
      specialtyStrings,
      ageStrings
    );

    const commands = [
      '/',
      `${URIPrefix.STATE}-${stateAbbreviation ? stateAbbreviation.toLocaleLowerCase() : EmptyURIValue}`,
      `${URIPrefix.CITY}-${city ? encodeForRouterSegment(city) : EmptyURIValue}`
    ];

    return filters ? [...commands, filters] : commands;
  }

  /**
   * Shared segment composition for both public forms, so the two can only differ by how a single
   * value is encoded.
   */
  private buildDirectoryPath(
    encode: SegmentEncoder,
    stateAbbreviation?: string,
    city?: string,
    insuranceTitle?: string,
    genderStrings?: string[],
    modalityStrings?: string[],
    specialtyStrings?: string[],
    ageStrings?: string[],
  ): string {
    let result = '';
    result = result.concat(`${this.createStateParam(stateAbbreviation)}`);
    result = result.concat(`${this.createCityParam(encode, city)}/`);

    return result.concat(this.buildFilterSegment(
      encode,
      insuranceTitle,
      genderStrings,
      modalityStrings,
      specialtyStrings,
      ageStrings
    ));
  }

  /**
   * The third path segment: insurance plus the comma-separated category lists.
   *
   * Each TYPE is separated by a period and each PARAM within a TYPE by a comma,
   * e.g. `ins-aetna.mod-gestalt,freudian.spc-trauma,personality`.
   */
  private buildFilterSegment(
    encode: SegmentEncoder,
    insuranceTitle?: string,
    genderStrings?: string[],
    modalityStrings?: string[],
    specialtyStrings?: string[],
    ageStrings?: string[],
  ): string {
    let result = insuranceTitle ? this.createInsuranceParam(insuranceTitle, encode) : '';

    const modalityParams = modalityStrings && modalityStrings.length > 0
      ? this.createModalitiesParam(modalityStrings, encode)
      : '';

    const specialtyParams = specialtyStrings && specialtyStrings.length > 0
      ? this.createSpecialtiesParam(specialtyStrings, encode)
      : '';

    const genderParams = genderStrings && genderStrings.length > 0
      ? this.createGendersParam(genderStrings, encode)
      : '';

    const ageParams = ageStrings && ageStrings.length > 0
      ? this.createAgesParam(ageStrings, encode)
      : '';

    // if there are modalities, specialties, or genders to include, pass that off to be handled
    // separately
    if (modalityParams || specialtyParams || genderParams || ageParams) {
      if (insuranceTitle) {
        result = result.concat('.');
      }
      result = result.concat(this.concatenateBonusParams([modalityParams, specialtyParams, genderParams, ageParams]));
    }
    return result;
  }

  // modality, specialty, and gender params are concatenated a bit differently.
  // where state, city, and insurance are all separated by slashes,
  // these param sets are comma-separated lists which are separated by periods
  // here, bonus param strings are "strings of extra parameters, themselves separated by commas
  concatenateBonusParams(bonusParams: string[]): string {
    // filter out everything empty and separate the results with periods
    const result = bonusParams
      .filter((param: string) => param?.trim())
      .join('.');

    // return with a slash before it to separate from earlier params
    return `${result}`;
  }

  private createCityParam(encode: SegmentEncoder, city?: string): string {
    const cityValue = city
      ? encode(city)
      : EmptyURIValue;

    // add a slash before the city param, which always has a value (even if it is the emptyUriValue)
    return `/${URIPrefix.CITY}-${cityValue}`;
  }

  private createGendersParam(genderStrings: string[], encode: SegmentEncoder): string {
    const commaSeparatedGenders = genderStrings
      .filter((gender) => !!gender)
      .map(
        (gender) => `${URIPrefix.GENDER}-${encode(gender)}`
      ).join(',');

    // add a period before the genders params
    return commaSeparatedGenders;
  }

  private createInsuranceParam(insuranceTitle: string, encode: SegmentEncoder): string {
    if (insuranceTitle) {
      const encodedInsurance = encode(insuranceTitle);

      // add a slash before the insurance param
      return `${URIPrefix.INSURANCE}-${encodedInsurance}`;
    }

    // return nothing if there's no title
    return '';
  }

  private createModalitiesParam(modalities: string[], encode: SegmentEncoder): string {
    const commaSeparatedModalities = modalities
      .filter((modality) => !!modality)
      .map(
        (modality) => `${URIPrefix.MODALITY}-${encode(modality)}`
      ).join(',');

    // add a period before the modalities params
    return commaSeparatedModalities;
  }

  private createAgesParam(ages: string[], encode: SegmentEncoder): string {
    const commaSeparatedAges = ages
      .filter((age) => !!age)
      .map(
        (age) => `${URIPrefix.AGE}-${encode(age)}`
      ).join(',');

    return commaSeparatedAges;
  }

  private createSpecialtiesParam(specialties: string[], encode: SegmentEncoder): string {
    // separate all of the specialties with commas
    const commaSeparatedSpecialties = specialties
      .filter((specialty) => !!specialty)
      .map(
        (specialty) => `${URIPrefix.SPECIALTY}-${encode(specialty)}`
      ).join(',');

    return `${commaSeparatedSpecialties}`;
  }

  private createStateParam(stateAbbreviation?: string): string {
    const stateValue = stateAbbreviation
      ? stateAbbreviation.toLocaleLowerCase()
      : EmptyURIValue;

    // add a slash before the state (state is always included, even as an emptyUriValue)
    return `/${URIPrefix.STATE}-${stateValue}`;
  }
}
